// WishDiaryLinks — 「連結的日記」section on `/wishes/:id`, with the link and
// unlink controls that were missing until W50.3 D-3.
//
// **What this section is for, precisely.** Before W50.3 the section rendered,
// but nothing on it did anything: the list only ever showed diaries that the
// backend had auto-created via `complete-with-diary`, and there was no way to
// attach an existing diary to a wish from either side. The backend endpoint that
// could do it (`PUT /diaries/{id}/source`) existed and was fully implemented —
// and also wrote a column the wishlist side never read, so even if it had been
// called the link would not have shown up here. Both halves are fixed in W50.3;
// this file is the entry point that was missing.
//
// It lives in its own file rather than inline in `WishDetail.tsx` for two
// reasons: AGENTS.md §3 #10's 15 KB route ceiling (that file was at 14.9 KB), and
// because the link control owns a query, two mutations and a sheet, which is
// more than a section header should carry.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink, Link2, Unlink } from "lucide-react";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import { LoadingState } from "@/components/primitives/LoadingState";
import { useToast } from "@/components/ui/toast";
import { Card } from "@/components/ui/card";
import { listDiaries, setDiarySource } from "@/lib/api/diary";
import { cn } from "@/lib/utils/cn";

export interface LinkedDiary {
  id: string;
  title: string | null;
  date: string;
}

const LINK_QUERY_KEY = "wishes:linked-diaries";

export function WishDiaryLinks({
  wishId,
  items,
}: {
  wishId: string;
  items: LinkedDiary[];
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: [LINK_QUERY_KEY, wishId] });
    void queryClient.invalidateQueries({ queryKey: ["diaries", "list"] });
  };

  // Success only, per ADR-0158's reasoning: the app-wide reporter owns failure.
  const linkMut = useMutation({
    mutationFn: (diaryId: string) =>
      setDiarySource(diaryId, { source_type: "wishlist", source_id: wishId }),
    onSuccess: () => {
      toast.push({ message: "已連結日記", variant: "you" });
      invalidate();
      setPickerOpen(false);
    },
  });

  const unlinkMut = useMutation({
    mutationFn: (diaryId: string) =>
      setDiarySource(diaryId, { source_type: null, source_id: null }),
    onSuccess: () => {
      toast.push({ message: "已解除連結", variant: "you" });
      invalidate();
    },
  });

  return (
    // W53.2 D-1 — was a hand-written `surface-plain border border-hairline
    // rounded-2xl p-s-4 flex flex-col gap-s-2 shadow-card`. Class-for-class the
    // same as `<Card>`'s default tier, which is precisely why it drifted instead
    // of being replaced: there was nothing to notice until one of them changed.
    <Card
      as="section"
      data-component="LinkedDiariesSection"
      data-testid="linked-diaries-section"
      className="p-s-4 flex flex-col gap-s-2"
    >
      <header className="flex items-center justify-between gap-s-2">
        <h2 className="font-display text-body font-semibold text-ink-1">連結的日記</h2>
        <div className="flex items-center gap-s-2">
          <span className="text-meta text-ink-3">{items.length} 篇</span>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            aria-label="連結一篇日記"
            data-testid="link-diary-open"
            className="press inline-flex items-center gap-1 min-h-11 px-s-3 rounded-full border border-hairline text-ink-1 text-meta font-semibold"
          >
            <Link2 className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            連結
          </button>
        </div>
      </header>

      {items.length === 0 ? (
        <p className="text-meta text-ink-3">
          還沒有連結的日記。可以按「連結」挑一篇，或在完成心願時選「寫一篇日記」自動建立。
        </p>
      ) : (
        <ul className="flex flex-col gap-s-2">
          {items.map((d) => (
            <li
              key={d.id}
              data-component="LinkedDiaryItem"
              data-testid={`linked-diary-${d.id}`}
              className="flex items-center gap-1"
            >
              <Link
                to={`/diaries/${d.id}`}
                className="press flex items-center gap-2 px-s-3 py-s-2 rounded-md hover:bg-paper-deep text-ink-1 flex-1 min-w-0"
              >
                <span className="font-semibold truncate flex-1">
                  {d.title || "（無標題）"}
                </span>
                <span className="text-meta text-ink-3 tabular-nums">{d.date}</span>
                <ExternalLink className="h-4 w-4 text-ink-3 shrink-0" aria-hidden />
              </Link>
              <button
                type="button"
                onClick={() => unlinkMut.mutate(d.id)}
                disabled={unlinkMut.isPending && unlinkMut.variables === d.id}
                aria-label={`解除連結 ${d.title || "（無標題）"}`}
                data-testid={`unlink-diary-${d.id}`}
                className="press inline-flex items-center justify-center min-h-11 min-w-11 rounded-full text-ink-2 disabled:opacity-40"
              >
                <Unlink className="h-4 w-4" strokeWidth={1.5} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <BottomSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        variant="mobile"
        title="選一篇日記連結到這個心願"
        description="連結後可以隨時解除。"
      >
        <DiaryPicker
          wishId={wishId}
          pendingId={linkMut.isPending ? linkMut.variables ?? null : null}
          disabledIds={items.map((i) => i.id)}
          onPick={(id) => linkMut.mutate(id)}
        />
      </BottomSheet>
    </Card>
  );
}

function DiaryPicker({
  wishId,
  onPick,
  pendingId,
  disabledIds,
}: {
  wishId: string;
  onPick: (diaryId: string) => void;
  pendingId: string | null;
  disabledIds: string[];
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["diaries", "list", "picker"],
    queryFn: () => listDiaries(null, 50),
    staleTime: 30_000,
  });

  if (isLoading) return <LoadingState shape="row" count={4} />;
  if (isError) {
    return <p className="text-body-sm text-ink-3 px-s-5 py-s-4">讀取日記失敗，請稍後再試。</p>;
  }

  const rows = data?.items ?? [];
  if (rows.length === 0) {
    return (
      <p className="text-body-sm text-ink-3 px-s-5 py-s-4">
        還沒有日記可以連結。先寫一篇再回來。
      </p>
    );
  }

  return (
    <ul className="flex flex-col" data-testid="diary-picker-list" data-wish-id={wishId}>
      {rows.map((d) => {
        const already = disabledIds.includes(d.id);
        return (
          <li key={d.id}>
            <button
              type="button"
              onClick={() => onPick(d.id)}
              disabled={already || pendingId === d.id}
              aria-label={
                already
                  ? `${d.title || "（無標題）"} 已連結`
                  : `連結 ${d.title || "（無標題）"}`
              }
              data-testid={`diary-picker-row-${d.id}`}
              className={cn(
                "press flex w-full items-center gap-3 px-s-5 min-h-12 text-left border-b border-hairline last:border-b-0",
                "disabled:opacity-40",
              )}
            >
              <span className="flex-1 min-w-0 truncate text-body-sm text-ink-1">
                {d.title || "（無標題）"}
              </span>
              <span className="text-meta text-ink-3 tabular-nums shrink-0">{d.date}</span>
              <span className="text-meta text-ink-2 shrink-0">
                {already ? "已連結" : pendingId === d.id ? "連結中…" : "連結"}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}